import { useState, useEffect, useCallback } from 'react';
import { Flex, Box } from '@chakra-ui/react';
import { LuX, LuClock, LuMapPin, LuWifi, LuCheck } from 'react-icons/lu';
import { FaInstagram } from 'react-icons/fa';
import type { MenuTheme, StoreInfo } from '../../api/menus';

export const ImageLightbox = ({ url, onClose }: { url: string; onClose: () => void }) => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <Flex position="fixed" inset="0" zIndex={9999} justifyContent="center" alignItems="center" backgroundColor="rgba(0,0,0,0.85)" onClick={onClose}>
      <Box position="absolute" top="12px" right="12px" cursor="pointer" p="8px" borderRadius="full" backgroundColor="rgba(255,255,255,0.15)" _hover={{ backgroundColor: 'rgba(255,255,255,0.25)' }} onClick={onClose}>
        <LuX size={20} color="#FFFFFF" />
      </Box>
      <Box maxWidth="90vw" maxHeight="85vh" borderRadius="12px" overflow="hidden" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt="" style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
      </Box>
    </Flex>
  );
};

export const StoreInfoPanel = ({ storeInfo, theme, strings, onClose }: { storeInfo: StoreInfo; theme: MenuTheme; strings: Record<string, string>; onClose: () => void }) => {
  const [copiedWifi, setCopiedWifi] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleCopyWifi = () => {
    if (!storeInfo.wifi_password) return;
    navigator.clipboard.writeText(storeInfo.wifi_password);
    setCopiedWifi(true);
    setTimeout(() => setCopiedWifi(false), 2000);
  };

  const instagramHandle = storeInfo.instagram?.replace(/^@/, '');

  return (
    <Flex position="fixed" inset="0" zIndex={9999} onClick={onClose}>
      <Box position="absolute" inset="0" backgroundColor="rgba(0,0,0,0.5)" />
      <Flex
        position="absolute"
        top="0"
        right="0"
        bottom="0"
        width={{ base: '62%', sm: '280px' }}
        direction="column"
        backgroundColor={theme.background_color}
        boxShadow="-4px 0 20px rgba(0,0,0,0.3)"
        onClick={(e) => e.stopPropagation()}
        css={{
          animation: 'slideInRight 0.25s ease-out',
          '@keyframes slideInRight': {
            from: { transform: 'translateX(100%)' },
            to: { transform: 'translateX(0)' },
          },
        }}
      >
        <Flex alignItems="center" justifyContent="space-between" px="20px" pt="20px" pb="16px">
          <Box fontSize="18px" fontWeight="600" color={theme.text_color}>{strings.storeInfo}</Box>
          <Box cursor="pointer" color={theme.secondary_text_color} _hover={{ color: theme.text_color }} onClick={onClose}>
            <LuX size={20} />
          </Box>
        </Flex>

        <Flex direction="column" px="20px" gap="4px">
          {storeInfo.store_hours && (
            <Flex gap="14px" alignItems="flex-start" py="12px" px="8px" borderRadius="8px">
              <Box color={theme.secondary_text_color} mt="2px" flexShrink={0}><LuClock size={18} /></Box>
              <Box fontSize="14px" fontWeight="400" color={theme.text_color} lineHeight="150%" whiteSpace="pre-line">{storeInfo.store_hours}</Box>
            </Flex>
          )}

          {storeInfo.address && (
            <Flex
              as={storeInfo.address_url ? 'a' : undefined}
              {...(storeInfo.address_url ? { href: storeInfo.address_url, target: '_blank', rel: 'noopener noreferrer' } : {})}
              gap="14px"
              alignItems="flex-start"
              py="12px"
              px="8px"
              borderRadius="8px"
              cursor={storeInfo.address_url ? 'pointer' : undefined}
              _hover={storeInfo.address_url ? { backgroundColor: `${theme.text_color}08` } : undefined}
              style={{ textDecoration: 'none' }}
            >
              <Box color={theme.secondary_text_color} mt="2px" flexShrink={0}><LuMapPin size={18} /></Box>
              <Box fontSize="14px" fontWeight="400" color={theme.text_color} lineHeight="150%">{storeInfo.address}</Box>
            </Flex>
          )}

          {storeInfo.wifi_password && (
            <Flex
              gap="14px"
              alignItems="center"
              py="12px"
              px="8px"
              borderRadius="8px"
              cursor="pointer"
              _hover={{ backgroundColor: `${theme.text_color}08` }}
              onClick={handleCopyWifi}
            >
              <Box color={theme.secondary_text_color} flexShrink={0}>
                {copiedWifi ? <LuCheck size={18} /> : <LuWifi size={18} />}
              </Box>
              <Box fontSize="14px" fontWeight="400" color={theme.text_color} lineHeight="150%" flex="1">{storeInfo.wifi_password}</Box>
              {copiedWifi && (
                <Box fontSize="12px" fontWeight="500" color={theme.secondary_text_color} flexShrink={0}>Copied!</Box>
              )}
            </Flex>
          )}

          {instagramHandle && (
            <a
              href={`https://instagram.com/${instagramHandle}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
            <Flex
              gap="14px"
              alignItems="center"
              py="12px"
              px="8px"
              borderRadius="8px"
              cursor="pointer"
              _hover={{ backgroundColor: `${theme.text_color}08` }}
            >
              <Box color={theme.secondary_text_color} flexShrink={0}><FaInstagram size={18} /></Box>
              <Box fontSize="14px" fontWeight="400" color={theme.text_color} lineHeight="150%">@{instagramHandle}</Box>
            </Flex>
            </a>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
